/**
 * Mahjong tile face atlas — loads `static/img/mahjong-atlas.png`, a 1536×2048
 * image built by `scripts/build-atlas.sh` from public-domain artwork:
 *   - 34 main faces (dots, bamboo, characters, winds, dragons) from
 *     FluffyStuff/riichi-mahjong-tiles (CC0).
 *   - 8 bonus faces (flowers, seasons) from Wikimedia Commons (CC0/CC-BY-SA).
 *
 * Layout: 8 columns × 8 rows of 192×256 cells (cell aspect 3:4 matches the
 * tile face aspect so images don't stretch). Per-tile materials clone the
 * texture with `offset`+`repeat` to pick their cell.
 */

import { LinearFilter, LinearMipmapLinearFilter, SRGBColorSpace, TextureLoader, type Texture } from 'three';
import type { Tile } from '../../engine/tile.js';

const COLS = 8;
const ROWS = 8;

const FACE_KEYS: string[] = [];
for (let v = 1; v <= 9; v++) FACE_KEYS.push(`dots-${v}`);
for (let v = 1; v <= 9; v++) FACE_KEYS.push(`bamboo-${v}`);
for (let v = 1; v <= 9; v++) FACE_KEYS.push(`characters-${v}`);
for (let v = 1; v <= 4; v++) FACE_KEYS.push(`wind-${v}`);
for (let v = 1; v <= 3; v++) FACE_KEYS.push(`dragon-${v}`);
for (let v = 1; v <= 4; v++) FACE_KEYS.push(`flower-${v}`);
for (let v = 1; v <= 4; v++) FACE_KEYS.push(`season-${v}`);

const FACE_INDEX = new Map<string, number>(FACE_KEYS.map((k, i) => [k, i]));

function faceKeyOf(tile: Tile): string {
  return `${tile.suit}-${tile.value}`;
}

export function uvForTile(tile: Tile): { offset: [number, number]; repeat: [number, number] } {
  const idx = FACE_INDEX.get(faceKeyOf(tile));
  if (idx === undefined) throw new Error(`unknown tile face: ${faceKeyOf(tile)}`);
  const col = idx % COLS;
  const row = Math.floor(idx / COLS);
  // Image texture: flipY=true (default) — canvas/image top maps to UV V=1.
  // Cell at row `row` (0-indexed from image top) → UV V range [(ROWS-1-row)/ROWS, (ROWS-row)/ROWS].
  return {
    offset: [col / COLS, (ROWS - 1 - row) / ROWS],
    repeat: [1 / COLS, 1 / ROWS],
  };
}

let _atlas: Texture | null = null;

export async function getTileAtlas(): Promise<Texture> {
  if (_atlas) return _atlas;
  const loader = new TextureLoader();
  const tex = await new Promise<Texture>((resolve, reject) => {
    loader.load('/img/mahjong-atlas.png', resolve, undefined, reject);
  });
  tex.colorSpace = SRGBColorSpace;
  tex.anisotropy = 8;
  // Atlas cells are tightly packed; mipmaps with linear filter avoid bleed
  // between adjacent cells when the texture is sampled at a distance.
  tex.minFilter = LinearMipmapLinearFilter;
  tex.magFilter = LinearFilter;
  tex.generateMipmaps = true;
  tex.needsUpdate = true;
  _atlas = tex;
  return tex;
}
